import React, { useCallback } from 'react';

/**
 * Upload component handles video file upload with drag-and-drop support
 * 
 * Features:
 * - Drag and drop file upload
 * - Click to select files
 * - File type validation (MP4, MOV, WebM)
 * - File size validation (500MB limit)
 * - Visual feedback for drag states
 */
const Upload = ({ onFileUpload, isProcessing }) => {
  /**
   * Handle file selection from input
   */
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      onFileUpload(file);
    }
  }, [onFileUpload]);

  /**
   * Handle drag and drop events
   */
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      onFileUpload(file);
    }
  }, [onFileUpload]);

  /**
   * Handle click to open file dialog
   */
  const handleClick = useCallback(() => {
    if (!isProcessing) {
      document.getElementById('file-input').click();
    }
  }, [isProcessing]);

  return (
    <div className="upload-container">
      <div
        className={`upload-area ${isProcessing ? 'processing' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{ 
          opacity: isProcessing ? 0.6 : 1,
          cursor: isProcessing ? 'not-allowed' : 'pointer'
        }}
      >
        <div className="upload-icon">📹</div>
        <div className="upload-text">
          {isProcessing ? 'Processing...' : 'Drop your video here or click to browse'}
        </div>
        <div className="upload-subtext">
          Supports MP4, MOV, WebM, AVI, MPEG, MKV files (max 500MB)
        </div>
      </div>
      
      <input
        id="file-input"
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/avi,video/x-msvideo,video/mpeg,video/x-mpeg,video/mp2t,video/x-matroska"
        onChange={handleFileSelect}
        className="file-input"
        disabled={isProcessing}
      />
      
      {isProcessing && (
        <div className="info-message">
          Please wait while we process your video...
        </div>
      )}
    </div>
  );
};

export default Upload;

